/**
 * Why the booking behind an email deep link (`/orders?order_id=…`) did not
 * appear — as one of three causes, not as one message for all of them.
 *
 * The distinction is the point. A single "could not be loaded" reads the same
 * whether the phone has no signal, the link points at a booking that no longer
 * exists, or the server answered with an error — and the customer's next move
 * is different in each case. At the KGSt-Forum a booking that was merely slow
 * to arrive was read as a broken link, because nothing on screen said which of
 * those it was.
 *
 * `util/axiosBaseQuery.ts` is what makes them separable:
 *
 *   - no response at all (no signal, timeout, server unreachable) leaves
 *     `status` unset, because there is no `err.response` to read it from;
 *   - an HTTP failure carries the numeric status;
 *   - a request that succeeded but returned no matching order is not an error
 *     at all — and is still a failure from where the customer stands.
 */
export type OrderLoadFailure = 'offline' | 'not_found' | 'server_error';

export interface OrderLoadOutcome {
    /**
     * The RTK Query error, if the request failed. Shaped by `axiosBaseQuery`:
     * `{ status?: number, data: unknown }` for an HTTP failure, or the raw
     * error for anything that never reached the network layer.
     */
    error?: { status?: unknown } | null;
    /** Whether the requested order came back. False after an empty result. */
    foundOrder: boolean;
}

/**
 * The HTTP status an error carries, or `null` when it carries none.
 *
 * Deliberately not `Number(raw)`: that turns `null` and `''` into 0, which is
 * finite, so a failure with no status at all would have been reported as a
 * server error — the one cause the customer can do nothing about, in place of
 * the one they can. A unit test caught exactly that.
 */
const httpStatusOf = (error: { status?: unknown }): number | null => {
    const raw = error.status;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    if (typeof raw !== 'string' || raw.trim() === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * `null` means "nothing to report" — the order is there.
 *
 * A failure without a status is treated as `offline`: that covers the
 * no-response case this exists for, and a non-Axios error (a bug in our own
 * code) lands there too. Telling the customer to check their connection is the
 * kinder of the two wrong answers, and the console still carries the real one.
 */
export const classifyOrderLoadFailure = (
    outcome: OrderLoadOutcome,
): OrderLoadFailure | null => {
    const { error, foundOrder } = outcome;

    if (error) {
        const status = httpStatusOf(error);
        if (status === null) return 'offline';
        if (status === 404) return 'not_found';
        return 'server_error';
    }

    return foundOrder ? null : 'not_found';
};

/**
 * Whether trying again can plausibly change the outcome. A retry button on
 * `not_found` would be a lie: the request arrived, the answer was "no such
 * booking", and pressing it again cannot produce one.
 */
export const failureIsRetryable = (failure: OrderLoadFailure): boolean =>
    failure !== 'not_found';

/** The i18n key carrying the message for this cause. */
export const failureMessageKey = (failure: OrderLoadFailure): string =>
    `orders.deep_link_error.${failure}`;
